import { useEffect, useState, type ReactElement } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { format, parseISO } from 'date-fns'
import { CalendarDays, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { CostInput, formatCostFromNumber, parseCostToNumber } from '@/shared/components/ui/CostInput'
import { useDeleteInvoice, useUpdateInvoice } from '../hooks/useInvoices'
import {
  updateInvoiceRequestSchema,
  type Invoice,
  type UpdateInvoiceRequest,
} from '../schemas/invoicesSchema'
import { InvoiceFilePreview } from './InvoiceFilePreview'

interface EditInvoiceDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  invoice: Invoice | null
}

export function EditInvoiceDialog({
  open,
  onOpenChange,
  invoice,
}: EditInvoiceDialogProps): ReactElement {
  const updateMutation = useUpdateInvoice()
  const deleteMutation = useDeleteInvoice()
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)

  const handleDelete = async (): Promise<void> => {
    if (!invoice) return
    try {
      await deleteMutation.mutateAsync(invoice.uuid)
      toast.success('Invoice deleted')
      setConfirmDeleteOpen(false)
      onOpenChange(false)
    } catch (error) {
      toast.error('Failed to delete invoice', {
        description: error instanceof Error ? error.message : 'Unknown error',
      })
    }
  }

  const form = useForm<UpdateInvoiceRequest>({
    resolver: zodResolver(updateInvoiceRequestSchema),
    defaultValues: {
      invoiceNumber: '',
      saleDate: '',
      netAmount: 0,
      grossAmount: 0,
      currency: '',
      counterparty: '',
    },
  })

  const netAmount = useWatch({ control: form.control, name: 'netAmount' })
  const grossAmount = useWatch({ control: form.control, name: 'grossAmount' })
  const currency = useWatch({ control: form.control, name: 'currency' })

  useEffect(() => {
    if (open && invoice) {
      form.reset({
        invoiceNumber: invoice.invoiceNumber,
        saleDate: invoice.saleDate,
        netAmount: invoice.netAmount,
        grossAmount: invoice.grossAmount,
        vatRate: invoice.vatRate != null ? invoice.vatRate * 100 : null,
        currency: invoice.currency,
        counterparty: invoice.counterparty,
        issueDate: invoice.issueDate,
        paymentDueDate: invoice.paymentDueDate,
      })
    }
  }, [open, invoice, form])

  const handleSubmit = async (values: UpdateInvoiceRequest): Promise<void> => {
    if (!invoice) return
    try {
      const payload = {
        ...values,
        vatRate: values.vatRate != null ? values.vatRate / 100 : null,
      }
      await updateMutation.mutateAsync({ uuid: invoice.uuid, body: payload })
      toast.success('Invoice updated')
      onOpenChange(false)
    } catch (error) {
      toast.error('Failed to update invoice', {
        description: error instanceof Error ? error.message : 'Unknown error',
      })
    }
  }

  const showPreview = open && !!invoice?.hasFile

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('sm:max-w-md', showPreview && 'sm:max-w-[90vw] lg:max-w-6xl')}>
        <DialogHeader>
          <DialogTitle>Edit Invoice</DialogTitle>
          <DialogDescription>
            {invoice?.invoiceNumber}
          </DialogDescription>
        </DialogHeader>

        <div className={cn(showPreview && 'flex flex-col sm:flex-row gap-4 sm:gap-6')}>
          {showPreview && (
            <div className="min-w-0 sm:w-[70%]">
              <InvoiceFilePreview
                invoiceUuid={invoice?.uuid}
                hasFile={invoice?.hasFile}
                originalFilename={invoice?.originalFilename ?? null}
              />
            </div>
          )}

          <div className={cn(showPreview && 'min-w-0 sm:w-[30%]')}>
            <Form {...form}>
              <form
                id="edit-invoice-form"
                onSubmit={form.handleSubmit(handleSubmit)}
                className="space-y-4"
              >
                <FormField
                  control={form.control}
                  name="invoiceNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Invoice Number</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="counterparty"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Counterparty</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="saleDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sale Date</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={cn(
                                'w-full px-3 py-2 h-9 flex items-center justify-start text-left font-normal',
                                !field.value && 'text-muted-foreground',
                              )}
                            >
                              <CalendarDays className="w-4 h-4 mr-2 text-muted-foreground" />
                              <span>
                                {field.value
                                  ? format(parseISO(field.value), 'dd MMM yyyy')
                                  : 'Pick a date'}
                              </span>
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={field.value ? parseISO(field.value) : undefined}
                            onSelect={date => {
                              field.onChange(date ? format(date, 'yyyy-MM-dd') : '')
                            }}
                            {...(field.value ? { defaultMonth: parseISO(field.value) } : {})}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <CostInput
                  label="Net Amount"
                  costValue={formatCostFromNumber(netAmount)}
                  onCostChange={val => {
                    const num = parseCostToNumber(val)
                    form.setValue('netAmount', num ?? 0, { shouldValidate: true })
                  }}
                  currencyValue={currency}
                  onCurrencyChange={val => form.setValue('currency', val, { shouldValidate: true })}
                />

                <CostInput
                  label="Gross Amount"
                  costValue={formatCostFromNumber(grossAmount)}
                  onCostChange={val => {
                    const num = parseCostToNumber(val)
                    form.setValue('grossAmount', num ?? 0, { shouldValidate: true })
                  }}
                  currencyValue={currency}
                  onCurrencyChange={val => form.setValue('currency', val, { shouldValidate: true })}
                />

                <FormField
                  control={form.control}
                  name="vatRate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>VAT Rate (%)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="23.00"
                          value={field.value ?? ''}
                          onChange={e => {
                            const val = e.target.value
                            field.onChange(val === '' ? null : parseFloat(val))
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </form>
            </Form>
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          <Button
            type="button"
            variant="destructive"
            onClick={() => setConfirmDeleteOpen(true)}
            disabled={updateMutation.isPending || deleteMutation.isPending}
          >
            <Trash2 className="size-4" />
            Delete
          </Button>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={updateMutation.isPending || deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="edit-invoice-form"
              disabled={updateMutation.isPending || deleteMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>

      <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete invoice?</DialogTitle>
            <DialogDescription className="whitespace-pre-line">
              {`This will permanently remove the invoice record, its attached file, and any links to payments.\n\nLinked payments will revert to needing an invoice. This cannot be undone.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmDeleteOpen(false)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => void handleDelete()}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  )
}
